import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react'

type RouterCtx = {
	path: string
	navigate: (to: string, opts?: { replace?: boolean }) => void
}

const Ctx = createContext<RouterCtx | null>(null)

// Dispatched on every in-app navigate() (not on browser back/forward, which
// fire popstate). The app shell listens for it to reset scroll on forward nav.
export const NAV_EVENT = 'hewey:navigate'

export const Router = ({ children }: { children: ReactNode }) => {
	const [path, setPath] = useState(() => window.location.pathname)

	useEffect(() => {
		const onPop = () => setPath(window.location.pathname)
		const onNav = () => setPath(window.location.pathname)
		window.addEventListener('popstate', onPop)
		window.addEventListener(NAV_EVENT, onNav)
		return () => {
			window.removeEventListener('popstate', onPop)
			window.removeEventListener(NAV_EVENT, onNav)
		}
	}, [])

	const navigate = useCallback<RouterCtx['navigate']>((to, opts) => {
		if (opts?.replace) window.history.replaceState(null, '', to)
		else window.history.pushState(null, '', to)
		window.dispatchEvent(new Event(NAV_EVENT))
	}, [])

	const value = useMemo(() => ({ path, navigate }), [path, navigate])
	return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export const useRouter = (): RouterCtx => {
	const ctx = useContext(Ctx)
	if (!ctx) throw new Error('useRouter must be used inside <Router>')
	return ctx
}
